/**
 * Cerebro de reglas de Pixel (sin IA): normaliza texto, detecta intenciones
 * (consultas y acciones), entiende filtros de fecha/cliente y referencias como
 * "el segundo", busca reportes con coincidencia difusa (priorizando los que más
 * abre el usuario), ordena pendientes por urgencia y decide cuándo saludar o recordar.
 * Todo es puro (sin React ni red) para poder probarlo con jest.
 */

const STOPWORDS = new Set([
    'a', 'al', 'de', 'del', 'el', 'la', 'las', 'los', 'lo', 'en', 'y', 'o', 'un', 'una', 'unos', 'unas',
    'para', 'por', 'con', 'que', 'me', 'mi', 'mis', 'tu', 'se', 'es', 'donde', 'esta', 'estan',
    'reporte', 'reportes', 'dashboard', 'dashboards', 'tablero', 'tableros',
    'buscar', 'busca', 'busco', 'encuentra', 'encontrar', 'abrir', 'abre', 'ver', 'muestra', 'muestrame',
    'quiero', 'necesito', 'dame', 'porfa', 'favor', 'pixel',
]);

export const REMIND_EVERY_MS = 2 * 60 * 60 * 1000;

/** Minúsculas, sin acentos ni signos; espacios colapsados. */
export function normalize(text) {
    return String(text || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9@\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/** Palabras útiles de la consulta (sin stopwords). */
export function queryTokens(text) {
    return normalize(text).split(' ').filter((word) => word && !STOPWORDS.has(word));
}

/** Distancia de Levenshtein con corte temprano (max). */
function editDistance(a, b, max) {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
    for (let i = 1; i <= a.length; i += 1) {
        const cur = [i];
        let rowMin = i;
        for (let j = 1; j <= b.length; j += 1) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
            rowMin = Math.min(rowMin, cur[j]);
        }
        if (rowMin > max) return max + 1;
        prev = cur;
    }
    return prev[b.length];
}

/** 1 si el token aparece tal cual, 0.6 si hay un typo tolerable, 0 si no. */
function tokenMatch(token, field) {
    if (!field) return 0;
    if (field.includes(token)) return 1;
    if (token.length < 4) return 0;
    const tolerance = token.length >= 7 ? 2 : 1;
    return field.split(' ').some((word) => editDistance(token, word.slice(0, token.length + tolerance), tolerance) <= tolerance)
        ? 0.6
        : 0;
}

const FIELD_WEIGHTS = [
    ['reporteNombre', 3],
    ['cliente', 2],
    ['proyecto', 2],
    ['descripcion', 1],
];

/** Cuánto sube un reporte en la búsqueda según las veces que el usuario lo ha abierto. */
export function usageBoost(usage, reportId) {
    return Math.log2(1 + (Number(usage?.[reportId]) || 0));
}

/** Suma una apertura al conteo de uso (devuelve un objeto nuevo). */
export function bumpUsage(usage, reportId) {
    if (!reportId) return usage || {};
    return { ...(usage || {}), [reportId]: (Number(usage?.[reportId]) || 0) + 1 };
}

/** Reportes que el usuario abre más (para "mis frecuentes"). */
export function topUsed(reports, usage, limit = 6) {
    return (reports || [])
        .filter((report) => report && !report.isDraft && (Number(usage?.[report.id]) || 0) > 0)
        .sort((a, b) => (usage[b.id] || 0) - (usage[a.id] || 0))
        .slice(0, limit);
}

/**
 * Busca reportes por nombre, cliente, proyecto o descripción.
 * Exige que cada palabra coincida en algún campo; desempata con los que el
 * usuario abre más (`usage`). Devuelve los mejores `limit`.
 */
export function searchReports(reports, text, limit = 6, usage = null) {
    const tokens = queryTokens(text);
    if (!tokens.length) return [];
    const phrase = tokens.join(' ');
    const seen = new Set();
    const scored = [];
    (reports || []).forEach((report) => {
        if (!report || report.isDraft || seen.has(report.id)) return;
        seen.add(report.id);
        const fields = FIELD_WEIGHTS.map(([key, weight]) => [normalize(report[key]), weight]);
        let score = 0;
        for (const token of tokens) {
            const best = Math.max(...fields.map(([value, weight]) => tokenMatch(token, value) * weight));
            if (best === 0) return;
            score += best;
        }
        if (fields[0][0].includes(phrase)) score += 2;
        if (fields[0][0].startsWith(phrase)) score += 1;
        score += usageBoost(usage, report.id) * 0.8;
        scored.push({ report, score });
    });
    return scored
        .sort((a, b) => b.score - a.score || String(a.report.reporteNombre || '').localeCompare(String(b.report.reporteNombre || ''), 'es'))
        .slice(0, limit)
        .map((row) => row.report);
}

const INTENTS = [
    ['help', /^(hola|hey|buen[oa]s?( dias| tardes| noches)?|ayuda|help|que (puedes|sabes) hacer|que haces|como funcionas?)( pixel)?$/],
    ['approve_action', /\b(aprueba|apruebalo|apruebala|apruebame|aprobarlo|aprobarla)\b/],
    ['validate_action', /\b(valida|validalo|validala)\b/],
    ['complete_action', /\b(completa|completalo|completala|resuelve|resuelvelo)\b|\bmarca\w*\b.*\b(completad[oa]|list[oa]|hech[oa]|resuelt[oa])\b|\bya (lo |la )?(hice|resolvi|termine|corregi)\b/],
    ['priority', /\b(que hago primero|por donde (empiezo|comienzo)|prioridad\w*|urgente\w*|lo mas importante|que es lo mas)\b/],
    ['favorites', /\b(frecuentes?|favoritos?|mas (uso|abro|visto\w*)|los de siempre|recientes?)\b/],
    ['mentions', /\b(mencion\w*|mencionaron|etiquet\w*|arroba)\b|@/],
    ['validate', /\bvalid\w*\b.*\bajuste|\bajuste\w*\b.*\bvalid/],
    ['approvals', /\b(aprob\w*|valid\w*|responsab\w*|deadline\w*|vencid\w*|vence\w*|firmar)\b/],
    ['adjustments', /\bajuste\w*\b/],
    ['notifications', /\b(notificacion\w*|aviso\w*|campana|novedades)\b/],
    ['summary', /\b(pendiente\w*|resumen|que tengo|tareas?|hoy|recordatorio\w*|al dia)\b/],
    ['account', /\b(mi cuenta|perfil|contrasena|foto)\b/],
    ['home', /\b(inicio|home|portada)\b/],
    ['creator', /\b(creador|crear (un )?(reporte|dashboard))\b/],
];

const OPEN_VERB = /\b(abre\w*|abrir|ver|veamos|muestra\w*|ensename|ir|ve al?)\b/;

/**
 * Intención principal del mensaje o 'search' si no hay atajo reconocible.
 * 'open_ref' = abrir algo de la lista anterior ("ábrelo", "abre el 2", "el último").
 */
export function detectIntent(text) {
    const clean = normalize(text);
    if (!clean) return 'help';
    for (const [name, pattern] of INTENTS.slice(0, 4)) {
        if (pattern.test(clean)) return name;
    }
    const ref = parseReference(clean);
    const onlyRef = clean.replace(REF_WORDS, ' ').replace(/\b(el|la|los|las|numero|num|no)\b/g, ' ').trim() === '';
    if ((ref.ordinal != null || ref.pronoun) && (OPEN_VERB.test(clean) || onlyRef || /^(abrelo|abrela)$/.test(clean))) {
        return 'open_ref';
    }
    for (const [name, pattern] of INTENTS.slice(4)) {
        if (pattern.test(clean)) return name;
    }
    return 'search';
}

/* ---------------------------------------------------------------------- */
/* Referencias a la lista anterior ("el segundo", "ábrelo")               */
/* ---------------------------------------------------------------------- */

const ORDINAL_WORDS = {
    primero: 1, primera: 1, primer: 1,
    segundo: 2, segunda: 2,
    tercero: 3, tercera: 3, tercer: 3,
    cuarto: 4, cuarta: 4,
    quinto: 5, quinta: 5,
    sexto: 6, sexta: 6,
    ultimo: 'last', ultima: 'last',
};
const REF_WORDS = /\b(primer[oa]?|segund[oa]|tercer[oa]?|cuart[oa]|quint[oa]|sext[oa]|ultim[oa]|abrelo|abrela|apruebalo|apruebala|validalo|validala|completalo|completala|resuelvelo|ese|esa|eso|\d{1,2})\b/g;
const PRONOUN = /\b(abrelo|abrela|apruebalo|apruebala|validalo|validala|completalo|completala|resuelvelo|ese|esa|eso)\b/;

/**
 * Detecta "el 2", "el segundo", "el último" o un pronombre ("ábrelo", "ese").
 * Los números solo cuentan como posición si van tras "el/la/número" o solos, y son ≤ 20.
 */
export function parseReference(text) {
    const clean = normalize(text);
    let ordinal = null;
    const word = clean.match(/\b(primer[oa]?|segund[oa]|tercer[oa]?|cuart[oa]|quint[oa]|sext[oa]|ultim[oa])\b/);
    if (word) ordinal = ORDINAL_WORDS[word[1]];
    const number = clean.match(/(?:^|\b(?:el|la|numero|num|no)\s+)(\d{1,2})(?:\s|$)/);
    if (ordinal == null && number && Number(number[1]) >= 1 && Number(number[1]) <= 20) ordinal = Number(number[1]);
    return { ordinal, pronoun: PRONOUN.test(clean) };
}

/** Elige un elemento de la lista anterior según la referencia; null si es ambiguo. */
export function pickFromList(list, ref) {
    const items = list || [];
    if (!items.length) return null;
    if (ref?.ordinal === 'last') return items[items.length - 1];
    if (typeof ref?.ordinal === 'number') return items[ref.ordinal - 1] || null;
    return items.length === 1 ? items[0] : null;
}

/* ---------------------------------------------------------------------- */
/* Filtros de fecha y cliente                                             */
/* ---------------------------------------------------------------------- */

const MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

/* Palabras que describen la consulta pero no el nombre de lo buscado. */
const FILTER_STOP = new Set([
    ...STOPWORDS, 'que', 'cual', 'cuales', 'tengo', 'hay', 'debo', 'tienen', 'mis', 'pendiente', 'pendientes',
    'aprobar', 'aprobacion', 'aprobaciones', 'aprueba', 'apruebalo', 'apruebala', 'responsable', 'responsables',
    'ajuste', 'ajustes', 'completa', 'completalo', 'completala', 'completado', 'valida', 'validalo', 'validala',
    'validar', 'marca', 'marcalo', 'como', 'listo', 'hecho', 'resuelto', 'resuelve', 'vence', 'vencen', 'vencido',
    'vencidos', 'vencida', 'vencidas', 'deadline', 'hoy', 'manana', 'semana', 'mes', 'este', 'proxima', 'siguiente',
    'asignados', 'asignado', 'ese', 'esa', 'eso', 'ya', 'hice', 'numero', 'num',
    ...MONTHS, ...Object.keys(ORDINAL_WORDS),
]);

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

/** Rango de fechas que menciona el texto ("hoy", "esta semana", "agosto"…) o null. */
export function parseRange(text, now = new Date()) {
    const clean = normalize(text);
    const today = startOfDay(now);
    if (/\bhoy\b/.test(clean)) return { from: today, to: addDays(today, 1), label: 'hoy' };
    if (/\bmanana\b/.test(clean)) return { from: addDays(today, 1), to: addDays(today, 2), label: 'mañana' };
    const weekday = (today.getDay() + 6) % 7; /* lunes = 0 */
    if (/\b(proxima|siguiente) semana\b/.test(clean)) {
        const monday = addDays(today, 7 - weekday);
        return { from: monday, to: addDays(monday, 7), label: 'la próxima semana' };
    }
    if (/\besta semana\b/.test(clean)) return { from: today, to: addDays(today, 7 - weekday), label: 'esta semana' };
    if (/\beste mes\b/.test(clean)) {
        return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: new Date(now.getFullYear(), now.getMonth() + 1, 1), label: 'este mes' };
    }
    const month = MONTHS.findIndex((name) => new RegExp(`\\b${name}\\b`).test(clean));
    if (month >= 0) {
        return { from: new Date(now.getFullYear(), month, 1), to: new Date(now.getFullYear(), month + 1, 1), label: MONTHS[month] };
    }
    return null;
}

/** Cliente mencionado en el texto (nombre canónico de `clients`) o null. Tolera typos. */
export function parseClient(text, clients = []) {
    const words = normalize(text).split(' ').filter(Boolean);
    let best = null;
    clients.forEach((client) => {
        const parts = normalize(client).split(' ').filter((part) => part.length > 1);
        if (!parts.length) return;
        const hit = parts.every((part) => words.some((word) => word === part
            || (part.length >= 5 && word.length >= 4 && editDistance(word, part, 1) <= 1)));
        if (hit && (!best || parts.length > best.parts)) best = { client, parts: parts.length };
    });
    return best ? best.client : null;
}

/**
 * Filtros de una consulta sobre listas de pendientes:
 * { client, range, overdueOnly, tokens } donde tokens son las palabras restantes
 * (para buscar por nombre del reporte o texto del ajuste).
 */
export function parseFilters(text, { clients = [], now = new Date() } = {}) {
    const client = parseClient(text, clients);
    const clientWords = new Set(client ? normalize(client).split(' ') : []);
    const tokens = normalize(text).split(' ')
        .filter((word) => word && !FILTER_STOP.has(word) && !clientWords.has(word) && !/^\d{1,2}$/.test(word));
    return {
        client,
        range: parseRange(text, now),
        overdueOnly: /\b(vencid[oa]s?|atrasad[oa]s?|retrasad[oa]s?)\b/.test(normalize(text)),
        tokens,
    };
}

const inRange = (iso, range) => {
    if (!range) return true;
    if (!iso) return false;
    const time = new Date(iso).getTime();
    return time >= range.from.getTime() && time < range.to.getTime();
};

const matchesTokens = (tokens, ...fields) => {
    if (!tokens.length) return true;
    const haystack = fields.map(normalize).join(' ');
    return tokens.every((token) => tokenMatch(token, haystack) > 0);
};

/** Filtra aprobaciones por cliente, rango de deadline, vencidas y nombre. */
export function filterApprovals(rows, filters) {
    return (rows || []).filter((row) => (
        (!filters.client || normalize(row.cliente) === normalize(filters.client))
        && (!filters.overdueOnly || row.overdue)
        && inRange(row.deadline, filters.range)
        && matchesTokens(filters.tokens, row.reportName, row.area)
    ));
}

/**
 * Filtra ajustes por cliente (vía el reporte), mes/rango de creación y texto.
 * `clientOf(reportId)` devuelve el cliente del reporte.
 */
export function filterAdjustments(rows, filters, clientOf = () => null) {
    return (rows || []).filter((row) => (
        (!filters.client || normalize(clientOf(row.reportId)) === normalize(filters.client))
        && inRange(row.createdAt, filters.range)
        && matchesTokens(filters.tokens, row.reportName, row.text)
    ));
}

/** Describe los filtros aplicados ("de BESINS, que vencen esta semana"). */
export function describeFilters(filters, { dateVerb = 'que vencen' } = {}) {
    const parts = [];
    if (filters.client) parts.push(`de ${filters.client}`);
    if (filters.overdueOnly) parts.push('vencidos');
    if (filters.range) parts.push(`${dateVerb} ${filters.range.label.startsWith('la ') || ['hoy', 'mañana', 'esta semana', 'este mes'].includes(filters.range.label) ? filters.range.label : `en ${filters.range.label}`}`);
    if (filters.tokens.length) parts.push(`con “${filters.tokens.join(' ')}”`);
    return parts.join(', ');
}

/** true si la consulta trae algún filtro. */
export function hasFilters(filters) {
    return !!(filters.client || filters.range || filters.overdueOnly || filters.tokens.length);
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** Frases cortas del resumen de pendientes (vacío si todo está al día). */
export function summaryLines(digest) {
    const c = digest?.counts;
    if (!c) return [];
    const lines = [];
    if (c.approvals) {
        const extra = [];
        if (c.approvalsOverdue) extra.push(`${c.approvalsOverdue} ${c.approvalsOverdue === 1 ? 'vencido' : 'vencidos'}`);
        if (c.approvalsDueSoon) extra.push(`${c.approvalsDueSoon} por vencer en menos de 24 h`);
        lines.push(`${plural(c.approvals, 'reporte', 'reportes')} por aprobar como responsable${extra.length ? ` (${extra.join(', ')})` : ''}.`);
    }
    if (c.adjustmentsAssigned) lines.push(`${plural(c.adjustmentsAssigned, 'ajuste asignado', 'ajustes asignados')} por resolver.`);
    if (c.adjustmentsToValidate) lines.push(`${plural(c.adjustmentsToValidate, 'ajuste', 'ajustes')} ya completado${c.adjustmentsToValidate === 1 ? '' : 's'} esperando tu validación.`);
    if (c.mentions) lines.push(`${plural(c.mentions, 'mención', 'menciones')} sin leer en comentarios.`);
    const otherUnread = (c.unread || 0) - (c.mentions || 0);
    if (otherUnread > 0) lines.push(`${plural(otherUnread, 'notificación', 'notificaciones')} sin leer.`);
    return lines;
}

/**
 * Total de cosas que requieren acción (badge y saludo). Las notificaciones
 * generales no cuentan: hay usuarios con cientos sin leer y el número pierde sentido.
 */
export function pendingTotal(digest) {
    const c = digest?.counts;
    if (!c) return 0;
    return (c.approvals || 0) + (c.adjustmentsAssigned || 0) + (c.adjustmentsToValidate || 0) + (c.mentions || 0);
}

/** Primer nombre para saludar. */
export function firstName(fullName) {
    const name = String(fullName || '').trim().split(/\s+/)[0];
    return name || '';
}

/** Saludo según la hora local. */
export function greetingFor(date = new Date()) {
    const hour = date.getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 19) return 'Buenas tardes';
    return 'Buenas noches';
}

/** Clave YYYY-MM-DD local (para "una vez al día"). */
export function dayKey(date = new Date()) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Cosas urgentes: aprobaciones vencidas o por vencer y menciones sin leer.
 * La firma cambia cuando aparece algo nuevo, para volver a avisar antes de tiempo.
 */
export function urgentItems(digest) {
    const approvals = (digest?.approvals || []).filter((row) => row.overdue || row.dueSoon);
    const mentions = digest?.mentions || [];
    const signature = [
        ...approvals.map((row) => `a:${row.responsibleId}:${row.overdue ? 'o' : 's'}`),
        ...mentions.map((row) => `m:${row.id}`),
    ].sort().join('|');
    return { approvals, mentions, count: approvals.length + mentions.length, signature };
}

/**
 * Decide si la mascota debe hablar sola.
 * state: { lastGreetDay, lastRemindAt, lastSignature, muteDay }.
 * Devuelve { kind: 'greet' | 'remind' | null, signature }.
 */
export function decideNudge(digest, state = {}, now = new Date()) {
    const today = dayKey(now);
    const urgent = urgentItems(digest);
    if (state.muteDay === today) return { kind: null, signature: urgent.signature };
    if (state.lastGreetDay !== today) return { kind: 'greet', signature: urgent.signature };
    if (!urgent.count) return { kind: null, signature: urgent.signature };
    const elapsed = now.getTime() - (Number(state.lastRemindAt) || 0);
    const isNew = urgent.signature !== state.lastSignature;
    if (isNew || elapsed >= REMIND_EVERY_MS) return { kind: 'remind', signature: urgent.signature };
    return { kind: null, signature: urgent.signature };
}

/** "3 de 4 ya aprobaron · solo faltas tú" (vacío si no hay datos). */
export function approvalProgress(row) {
    const total = Number(row?.totalResponsibles) || 0;
    const done = Number(row?.approvedCount) || 0;
    if (total <= 1) return total === 1 ? 'Eres el único responsable' : '';
    const text = `${done} de ${total} ya aprobaron`;
    return done === total - 1 ? `${text} · solo faltas tú` : text;
}

/**
 * Pendientes ordenados por urgencia para "¿qué hago primero?":
 * aprobaciones vencidas → por vencer (< 24 h) → donde solo faltas tú → ajustes
 * asignados (más viejos primero) → validaciones → menciones → resto de aprobaciones.
 * Cada elemento: { kind, row, reason }.
 */
export function priorityItems(digest, now = new Date(), limit = 6) {
    const approvals = digest?.approvals || [];
    const onlyYou = (row) => (Number(row.totalResponsibles) || 0) > 1 && row.approvedCount === row.totalResponsibles - 1;
    const byDeadline = (a, b) => String(a.deadline || '9999').localeCompare(String(b.deadline || '9999'));
    const approvalItem = (row) => ({
        kind: 'approval',
        row,
        reason: [relativeDeadline(row.deadline, now), approvalProgress(row)].filter(Boolean).join(' · '),
    });
    const used = new Set();
    const take = (rows) => rows.filter((row) => {
        if (used.has(row.responsibleId)) return false;
        used.add(row.responsibleId);
        return true;
    });
    const items = [
        ...take(approvals.filter((row) => row.overdue).sort(byDeadline)).map(approvalItem),
        ...take(approvals.filter((row) => row.dueSoon).sort(byDeadline)).map(approvalItem),
        ...take(approvals.filter(onlyYou).sort(byDeadline)).map(approvalItem),
        ...[...(digest?.adjustmentsAssigned || [])]
            .sort((a, b) => String(a.createdAt || '').localeCompare(String(b.createdAt || '')))
            .map((row) => ({ kind: 'assigned', row, reason: `Ajuste asignado: “${row.text}”` })),
        ...(digest?.adjustmentsToValidate || []).map((row) => ({ kind: 'validate', row, reason: `Por validar: “${row.text}”` })),
        ...(digest?.mentions || []).map((row) => ({ kind: 'mention', row, reason: 'Te mencionaron' })),
        ...take([...approvals].sort(byDeadline)).map(approvalItem),
    ];
    return items.slice(0, limit);
}

/** Texto corto del globo de recordatorio (nombra el caso más urgente). */
export function reminderText(digest, now = new Date()) {
    const { approvals, mentions } = urgentItems(digest);
    const first = [...approvals].sort((a, b) => (a.overdue === b.overdue ? 0 : a.overdue ? -1 : 1))[0];
    if (first) {
        const when = relativeDeadline(first.deadline, now).toLowerCase();
        const progress = approvalProgress(first);
        const others = approvals.length - 1 + mentions.length;
        const tail = others > 0 ? ` Y ${plural(others, 'pendiente urgente más', 'pendientes urgentes más')}.` : '';
        return `“${first.reportName}” ${when}${progress ? ` (${progress.toLowerCase()})` : ''}.${tail}`;
    }
    return mentions.length ? `Ojo: tienes ${plural(mentions.length, 'mención', 'menciones')} sin leer.` : '';
}

/** Fecha relativa amigable ("vence en 5 h", "venció hace 2 días"). */
export function relativeDeadline(isoDate, now = new Date()) {
    if (!isoDate) return 'Sin deadline';
    const diff = new Date(isoDate).getTime() - now.getTime();
    const abs = Math.abs(diff);
    const hours = Math.round(abs / 3600000);
    const days = Math.round(abs / 86400000);
    const span = hours < 1 ? 'menos de 1 h' : hours < 36 ? `${hours} h` : plural(days, 'día', 'días');
    return diff < 0 ? `Venció hace ${span}` : `Vence en ${span}`;
}
